import React, { useMemo, useState } from 'react';
import { App, Button, Card, Col, Descriptions, Row, Space, Spin, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, EditOutlined, PrinterOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PrintableHrmsTransaction } from '@/components/hr/PrintableHrmsTransaction';
import { ENTITY_TYPE_HR_SALARY_REVISION } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

type RevisionLine = { component_code: string; component_name: string; amount: number };

const SalaryRevisionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions();
  const [loading, setLoading] = useState<string | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.salaryRevision(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getSalaryRevision(id!),
        () => hrmsService.listSalaryRevisions(),
        id!
      ),
    enabled: Boolean(id),
  });

  const lines: RevisionLine[] = useMemo(() => {
    const raw = row?.lines_json;
    return Array.isArray(raw) ? raw : [];
  }, [row?.lines_json]);

  const linesTotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [lines]
  );

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Pending', 'Approved', 'Applied'], row?.status || 'Draft'),
    [row?.status]
  );

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(ok);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryRevision(id!) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryRevisions() });
    } catch {
      message.error(`${ok} failed`);
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Salary revision" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Salary revision" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const printDoc = () => {
    openHrmsPrintOverlay((onClose) => (
      <PrintableHrmsTransaction
        onClose={onClose}
        data={{
          docType: 'HR_SALARY_REVISION',
          title: 'Salary Revision',
          documentNumber: row.revision_number,
          employee_name: empLabel(row.employee_id),
          status: row.status,
          meta: [
            { label: 'Type', value: String(row.revision_type || '—') },
            {
              label: 'Effective',
              value: row.effective_from ? dayjs(row.effective_from).format('DD-MMM-YYYY') : '—',
            },
          ],
          lines: [
            ...(lines.length
              ? lines.map((l) => ({
                  description: `${l.component_code} — ${l.component_name}`,
                  value: String(l.amount ?? '—'),
                }))
              : [
                  { description: 'Previous basic', value: String(row.previous_basic ?? '—') },
                  { description: 'New basic', value: String(row.new_basic ?? '—') },
                ]),
            { description: 'Reason', value: String(row.reason || '—') },
          ],
          notes: row.reason,
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Salary revision"
          documentNumber={row.revision_number || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/salary-revisions')}>
                Back
              </Button>
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print
              </Button>
              {row.status === 'Draft' && (
                <>
                  <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/salary-revisions/${id}/edit`)}>
                    Edit
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={loading === 'submit'}
                    onClick={() => run('submit', () => hrmsService.submitSalaryRevision(id!), 'Submitted')}
                  >
                    Submit
                  </Button>
                </>
              )}
              {(row.status === 'Approved' || row.status === 'Pending') && (
                <Button
                  icon={<CheckOutlined />}
                  loading={loading === 'apply'}
                  onClick={() => run('apply', () => hrmsService.applySalaryRevision(id!), 'Applied')}
                >
                  Apply
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Details" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Type">{row.revision_type}</Descriptions.Item>
              <Descriptions.Item label="Effective">
                {row.effective_from ? dayjs(row.effective_from).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Previous basic">{row.previous_basic ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="New basic">{row.new_basic ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Heads total">
                {lines.length ? linesTotal.toLocaleString() : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>
                {row.reason || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="Salary heads" style={{ marginBottom: 16 }}>
            {lines.length ? (
              <Table
                size="small"
                rowKey={(r) => r.component_code}
                pagination={false}
                dataSource={lines}
                columns={[
                  { title: 'Code', dataIndex: 'component_code', width: 120 },
                  { title: 'Salary head', dataIndex: 'component_name' },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    width: 120,
                    align: 'right',
                    render: (v: number) => Number(v).toLocaleString(),
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{linesTotal.toLocaleString()}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            ) : (
              <Text type="secondary">
                No salary-head lines on this revision (legacy basic-only document).
              </Text>
            )}
          </Card>
          <EntityAttachmentsCard entityType="HR_SALARY_REVISION" entityId={row.id} />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_SALARY_REVISION}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.salaryRevision(id!), hrmsKeys.salaryRevisions()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default SalaryRevisionDetailPage;
